import React, { useEffect } from "react"
import CollapsibleSection from "../CollapsibleSection"
import BasicInfoSection from "../../BasicInfoSection"
import AddressSection from "../../AddressSection"
import IndustrySection from "../../IndustrySection"
import { BusinessDetails } from "../../types"
import { industryOptions } from "../../constants"
import { Button } from "@/components/ui/button"

interface SectionManagerProps {
  businessDetails: BusinessDetails
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleSelectChange: (value: string, field: string) => void
  selectedDate?: Date
  calendarOpen: boolean
  setCalendarOpen: (open: boolean) => void
  handleDateSelect: (date: Date | undefined) => void
  nameExceeds30Chars: boolean
  shortNameLength: number
  shortNameManuallyEdited: boolean
  showAddressSection: boolean
  showIndustrySection: boolean
  basicInfoOpen: boolean
  setBasicInfoOpen: (open: boolean) => void
  addressOpen: boolean
  setAddressOpen: (open: boolean) => void
  industryOpen: boolean
  setIndustryOpen: (open: boolean) => void
  handleWebsiteBlur: () => void
  onSaveBasicInfo: () => void
  onSaveAddress: () => void
  onSaveIndustry: () => void
  basicInfoComplete: boolean
  addressComplete: boolean
  industryComplete: boolean
  basicInfoEdited: boolean
  addressEdited: boolean
  industryEdited: boolean
  basicInfoHasChanges?: boolean
  addressHasChanges?: boolean
  industryHasChanges?: boolean
  handleSubmit?: () => void
  errors?: {
    phone?: string
    email?: string
    website?: string
  }
}

const SectionManager: React.FC<SectionManagerProps> = ({
  businessDetails,
  handleInputChange,
  handleSelectChange,
  selectedDate,
  calendarOpen,
  setCalendarOpen,
  handleDateSelect,
  nameExceeds30Chars,
  shortNameLength,
  shortNameManuallyEdited,
  showAddressSection,
  showIndustrySection,
  basicInfoOpen,
  setBasicInfoOpen,
  addressOpen,
  setAddressOpen,
  industryOpen,
  setIndustryOpen,
  handleWebsiteBlur,
  onSaveBasicInfo,
  onSaveAddress,
  onSaveIndustry,
  basicInfoComplete,
  addressComplete,
  industryComplete,
  basicInfoEdited,
  addressEdited,
  industryEdited,
  basicInfoHasChanges = false,
  addressHasChanges = false,
  industryHasChanges = false,
  handleSubmit,
  errors,
}) => {
  // Add logging to help debug
  useEffect(() => {
    console.log("SectionManager - basicInfoComplete:", basicInfoComplete)
    console.log("SectionManager - basicInfoEdited:", basicInfoEdited)
  }, [basicInfoComplete, basicInfoEdited])

  return (
    <>
      <CollapsibleSection
        title="General Info"
        open={basicInfoOpen}
        onOpenChange={setBasicInfoOpen}
      >
        <div className="space-y-6">
          <BasicInfoSection
            businessDetails={businessDetails}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            selectedDate={selectedDate}
            calendarOpen={calendarOpen}
            setCalendarOpen={setCalendarOpen}
            handleDateSelect={handleDateSelect}
            nameExceeds30Chars={nameExceeds30Chars}
            shortNameLength={shortNameLength}
            shortNameManuallyEdited={shortNameManuallyEdited}
            handleWebsiteBlur={handleWebsiteBlur}
            errors={errors}
          />

          {basicInfoComplete && !basicInfoEdited && (
            <Button onClick={onSaveBasicInfo} className="w-full">
              {basicInfoHasChanges ? "Save Changes" : "Continue"}
            </Button>
          )}

          {basicInfoEdited && basicInfoHasChanges && (
            <Button onClick={onSaveBasicInfo} className="w-full">
              Save Changes
            </Button>
          )}
        </div>
      </CollapsibleSection>

      {showAddressSection && (
        <CollapsibleSection
          title="Location"
          open={addressOpen}
          onOpenChange={setAddressOpen}
        >
          <div className="space-y-6">
            <AddressSection
              businessDetails={businessDetails}
              handleInputChange={handleInputChange}
              handleSelectChange={handleSelectChange}
            />

            {addressComplete && !addressEdited && (
              <Button onClick={onSaveAddress} className="w-full">
                {addressHasChanges ? "Save Changes" : "Continue"}
              </Button>
            )}

            {addressEdited && addressHasChanges && (
              <Button onClick={onSaveAddress} className="w-full">
                Save Changes
              </Button>
            )}
          </div>
        </CollapsibleSection>
      )}

      {showIndustrySection && (
        <div className="mt-6 mb-3">
          <div className="mb-2 font-medium">Select Industry</div>
          <IndustrySection
            businessDetails={businessDetails}
            handleSelectChange={handleSelectChange}
            industryOptions={industryOptions}
            onSaveIndustry={onSaveIndustry}
            industryComplete={industryComplete}
            handleSubmit={handleSubmit}
          />
        </div>
      )}
    </>
  )
}

export default SectionManager
